/**
 * This Nuxt plugin handles user activity tracking and session validation based on inactivity.
 * It tracks user interactions (mouse movement or key presses) to determine if the user is active.
 * After a specified period of inactivity (e.g., 5 minutes), it checks if the user is still logged in.
 *
 * The plugin also manages setting and clearing inactivity timers, ensuring checks are made at regular intervals
 * while the user is inactive.
 * 
 * Plugin lifecycle:
 * - When the page is loaded, the inactivity check starts after a short initial delay.
 * - If the user is inactive for a certain amount of time, a request is sent to check if the user is still logged in.
 * - When user activity is detected (mouse move or key press), the inactivity timer is reset.
 * - Event listeners are removed, and timers are cleared when the plugin is unloaded.
 * 
 * @module UserActivityTracker
 */
export default defineNuxtPlugin((nuxtApp: NuxtApp) => {
    console.log('[Keep-alive]: Init.');
    const inactivityLimit: number = 10000; // 5 minutes (300,000 ms)
    const checkInterval: number = 5000; // Interval between inactivity checks, e.g., every 5 minutes
    let inactivityTimeout: number | undefined;
    let lastActiveTime = Date.now(); // Keeps track of the last activity timestamp

    const { execute, data, status, error } = useApiData(
        'get-account-check-session',
        '/account/check-session',
        { immediate: false },
        {
            method: 'GET',
            credentials: 'include'
        });

    /**
     * Function to check if the user is still logged in.
     * Sends a request to the server to verify the user's session status.
     * If the session is expired or the user is not logged in, it handles logout behavior.
     *
     * @async
     * @returns {Promise<void>} - A promise that resolves after the session check completes
     */
    const checkIfUserIsLoggedIn = async (): Promise<void> => {
        try {
            await execute();

            if (data.value.status === 204) {
                nuxtApp.$authentication.setUser();
            }
        } catch (error) {
            console.error('Error during session check:', error);
        }
    };

    /**
     * Function that runs when user activity is detected.
     * It checks if the user has been inactive for longer than the inactivityLimit.
     * If so, it calls checkIfUserIsLoggedIn() to validate if the session is still active.
     * This function also resets the inactivity timer to trigger the next check after checkInterval.
     */
    const setUserActive = () => {
        const now = Date.now();

        // If the user has been inactive for more than 5 minutes, check if they are still logged in
        if (now - lastActiveTime > inactivityLimit) {
            checkIfUserIsLoggedIn();
            lastActiveTime = now; // Reset the last activity timestamp after the check
        }

        // Start a new timeout that checks activity after a given interval
        inactivityTimeout = setTimeout(setUserActive, checkInterval);
    };

    /**
    * Function that resets the inactivity timer when user activity is detected.
    * It updates the last active time and clears the previous inactivity timeout,
    * then starts a new timeout for the next inactivity check.
    */
    const resetInactivityTimer = () => {
        lastActiveTime = Date.now(); // Update the last activity timestamp

        if (inactivityTimeout) {
            clearTimeout(inactivityTimeout); // Clear the previous inactivity timeout if the user is active
        }

        // Start a new inactivity timeout for the next check
        inactivityTimeout = setTimeout(setUserActive, checkInterval);
    };

    /**
     * Starts the activity check when the page is loaded.
     * The check will run at the specified interval (checkInterval) to monitor user inactivity.
     * Event listeners for mouse movement and key presses are also added when the user is authenticated.
     * 
     * The check will be stopped if the user is logged out or the session is invalid.
     */
    nuxtApp.hook('app:mounted', () => {
        const {
            authenticated
        } = storeToRefs(nuxtApp.$authentication);

        watch(authenticated, (value: boolean) => {
            if (value) {
                inactivityTimeout = setTimeout(setUserActive, checkInterval); // Start the check after a short initial wait
                window.addEventListener('mousemove', resetInactivityTimer);
                window.addEventListener('keydown', resetInactivityTimer);
            } else {
                clearTimeout(inactivityTimeout);
                window.removeEventListener('mousemove', resetInactivityTimer);
                window.removeEventListener('keydown', resetInactivityTimer);
            }
        }, { immediate: true });
    });

    /**
     * Clean up event listeners and inactivity timers before the plugin is removed.
     * This ensures that no unnecessary listeners or timers are left running, avoiding potential memory leaks.
     */
    nuxtApp.hook('app:beforeUnmount', () => {
        clearTimeout(inactivityTimeout);
        window.removeEventListener('mousemove', resetInactivityTimer);
        window.removeEventListener('keydown', resetInactivityTimer);
    });
});
